import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Highcharts from 'highcharts';
import axios from 'axios';

import { customSort, getApiServrUrl, getLastDateOfMonth, getGoogleAnalyticsId, getNumberOfDaysBetweenDates } from '../../utils/utils';
import { lineColors } from '../../utils/constants';
import { DownloadButton } from '../common/download_button';
import Select from 'react-select';

import { observer } from 'mobx-react';
import { metadataStore } from '../../stores/metadata_store';
import { growthDataStore } from '../../stores/growth_data_store';
import { dataVersionDateStore } from '../../stores/data_version_date_store';
import LoadingWithText from '../common/loading_text';
import { userAccountDataStore } from '../../stores/user_account_data_store';
import Dropdown from '../common/collections_dropdown';
import { collectionsStore } from '../../stores/collections_store';
import { toJS } from 'mobx';
import { apiEventsStore } from '../../stores/api_events_store';
import { mixpanelActions } from '../../utils/mixpanel_util';
import { amplitudeActions } from '../../utils/amplitude_util';


const CompanyGrowthChartComponent = observer(() => {
    const [data, setData] = useState(null);
    const [universeAvgData, setUniverseAvgData] = useState(null);
    const [additionData, setAdditionData] = useState(null);
    const [attritionData, setAttritionData] = useState(null);
    const [additionAttritionDate, setAdditionAttritionDate] = useState('');
    const [collections, setCollections] = useState([]);
    
    const [geo1Values, setGeo1Values] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState({value: 'All', label: 'All'});

    const [showUniverseAvgs, setShowUniverseAvgs] = useState(false);
    const [showHiringForGrowthChart, setShowHiringForGrowthChart] = useState(true);
    const [showAttritionForGrowthChart, setShowAttritionForGrowthChart] = useState(true);
    const [showNetGrowthForGrowthChart, setShowNetGrowthForGrowthChart] = useState(true);
    const [showGrowthProdForGrowthChart, setShowGrowthProdForGrowthChart] = useState(true);
    const [showIndustry, setShowIndustry] = useState(false);

    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isMetaDataLoading, setIsMetaDataLoading] = useState(false);
    const [isAdditionAttritionLoading, setIsAdditionAttritionLoading] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [publicProfileCount, setPublicProfileCount] = useState(0);
    const [isUnAuthorized, setIsUnAuthorized] = useState(false);    
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const universeId = searchParams && searchParams.get('universe_id') || location.state && location.state.universeId;
    const companyId = searchParams && searchParams.get('company_id') || location.state && location.state.companyId;
    const accessToken = searchParams && searchParams.get('access_token') || location.state && location.state.accessToken;
    const dataset = searchParams && searchParams.get('dataset') || location.state && location.state.dataset;

    let companyGrowthPath = `/company/growth?company_id=${companyId}`;
    let teamGrowthPath = `/company/team/growth?company_id=${companyId}`; 
    let talentFlowPath = `/company/talent?company_id=${companyId}`;
    let tenurePath = `/company/tenure?company_id=${companyId}`;
    let geoPath = `/company/geo?company_id=${companyId}`;

    if (accessToken !== null && accessToken !== undefined) {
        companyGrowthPath += `&access_token=${accessToken}`;
        teamGrowthPath += `&access_token=${accessToken}`;
        talentFlowPath += `&access_token=${accessToken}`;
        tenurePath += `&access_token=${accessToken}`;
        geoPath += `&access_token=${accessToken}`;
    }

    if (dataset !== null && dataset !== undefined) {
        companyGrowthPath += `&dataset=${dataset}`;
        teamGrowthPath += `&dataset=${dataset}`;
        talentFlowPath += `&dataset=${dataset}`;
        tenurePath += `&dataset=${dataset}`;
        geoPath += `&dataset=${dataset}`;
    }

    let universeSummaryPath = '';

    if (universeId !== null && universeId !== undefined) {
        companyGrowthPath += `&universe_id=${universeId}`;
        teamGrowthPath += `&universe_id=${universeId}`;
        talentFlowPath += `&universe_id=${universeId}`;
        tenurePath += `&universe_id=${universeId}`;
        geoPath += `&universe_id=${universeId}`;

        universeSummaryPath = `/universe/summary?universe_id=${universeId}`;
    }
    
    const sortOrder = {
        'owner': 0,
        'executive': 1,
        'vice president': 2,
        'director': 3,
        'manager': 4,
        'individual contributor': 5,
        'assistant': 6,
        'trainee': 7,
        'student / intern': 8,
        'unsure': 9,

        'Executive': 0,
        'Manager / Director': 3,
        'Individual Contributor': 5,
        'Intern': 8,
        'Other': 9,

        // new mappings 2023-03-15
        'pct_executive': 0,
        'pct_advisor_board': 1,
        'pct_leadership': 2,
        'pct_management': 3,
        'pct_individual_contributor': 4,
        'pct_student___intern': 5,
    };

    const getMetaData = async() => {
        // await both promises in parallel
        await Promise.all([
            metadataStore.fetchMetadata(companyId, accessToken, dataset),
            dataVersionDateStore.fetchDataVersionDate(accessToken),
        ]);
        if (!metadataStore.isUnAuthorized) {
            setAdditionAttritionDate(new Date(dataVersionDateStore.dataVersionDate));
            
            setCompanyName(metadataStore.data[companyId].companyName);
            if (metadataStore.data[companyId].publicProfileCount !== undefined) {
                setPublicProfileCount(metadataStore.data[companyId].publicProfileCount);
            }
        }
        setIsUnAuthorized(metadataStore.isUnAuthorized);
        setIsMetaDataLoading(false);
    };

    const getGrowthData = async() => {
        
        try {
            await growthDataStore.fetchGrowthData(companyId, universeId, accessToken, dataset);
            const jsonData = growthDataStore.data[companyId];
            setData(jsonData);
            let geo1Values = [... new Set(jsonData.map((item) => item.geo1))];
            let sortedGeo1Values = customSort(geo1Values, ['All', 'North America', 'Europe, Middle East & Africa', 'Latin America', 'Asia/Pacific']);
            setGeo1Values(sortedGeo1Values);

            if (userAccountDataStore.user !== null && userAccountDataStore.user !== undefined) {
                await collectionsStore.fetchCollections(userAccountDataStore.user.email, companyId);
                setCollections(toJS(collectionsStore.collections[companyId]));
            };
           
            setIsUnAuthorized(growthDataStore.setUnAuthorized);
        } catch (err) {
            console.log(err);
            if (err.response !== undefined && (err.response.status == 401 || err.response.status == 403)) {
                setIsUnAuthorized(true);
            }
            
        }
        
        if (universeId !== null && universeId !== undefined) {
            try {
                const universeAvgData = growthDataStore.universeData[universeId];
                setUniverseAvgData(universeAvgData);
                setIsUnAuthorized(growthDataStore.setUnAuthorized);
            } catch (err) {
                console.log(err);
            }
        }

        setIsPageLoading(false);
    };

    const getAdditionsAttritionsData = async() => {
        if (additionAttritionDate !== '') {
            await Promise.all([
                getAdditionsData(dataset),
                getAttritionsData(dataset),
            ]);
        }
    };
    
    const getAdditionsData = async() => {
        let apiUrl = getApiServrUrl();
        apiUrl += process.env.REACT_APP_API_ADDITIONS_ENDPOINT;
        console.log(`CompanyGrowth: Fetching growth additions data from ${apiUrl}...`);
        
        let data = {
            "company_ids": [parseInt(companyId)],
            "dataset": dataset,
            "year": additionAttritionDate.getUTCFullYear(),
            "month": additionAttritionDate.getUTCMonth() + 1,
        }
        let config = {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken,
            },
            decompress: true,
            //withCredentials: true,
        };
        if (accessToken === null || accessToken === undefined || accessToken === '') {
            config['withCredentials'] = true;
        }

        try {
            setIsAdditionAttritionLoading(true);
            const response = await axios.post(apiUrl, data, config);
            let dateStr =  '';
            dateStr += additionAttritionDate.getUTCFullYear();
            if (additionAttritionDate.getUTCMonth() + 1  < 10) {
                dateStr += '-0' + (additionAttritionDate.getUTCMonth() + 1);
            }
            else {
                dateStr += '-' + (additionAttritionDate.getUTCMonth() + 1);
            }
            if (additionAttritionDate.getUTCDate() < 10) { 
                dateStr += '-0' + additionAttritionDate.getUTCDate();
            }
            else { 
                dateStr += '-' + additionAttritionDate.getUTCDate();
            }
            if (response.status == 200 && response.data.data.headcount_additions[companyId][dateStr] !== undefined) {
                const additionData = response.data.data.headcount_additions[companyId][dateStr];
                const additionDataSorted = additionData.sort((a, b) => {
                    return sortOrder[a.seniority] - sortOrder[b.seniority];
                });
                setAdditionData(additionDataSorted.slice(0, 25));

                if (userAccountDataStore.user !== null) {
                    // track growth event
                    apiEventsStore.trackApiEvents(userAccountDataStore.user.uuid, {
                        companyId,
                        eventType: 'growth',
                        eventData: {
                            "url": apiUrl,
                            "path": process.env.REACT_APP_API_ADDITIONS_ENDPOINT,
                            "dataset": dataset,
                            "user_agent": navigator.userAgent,
                        },
                    });
                }
            }
            else {
                setAdditionData(null);
            }

            
        } catch (err) {
            console.log(err);
        }
    };

    const getAttritionsData = async() => {
        let apiUrl = getApiServrUrl();
        apiUrl += process.env.REACT_APP_API_ATTRITIONS_ENDPOINT;
        console.log(`CompanyGrowth: Fetching growth attritions data from ${apiUrl}...`);
        
        let data = {
            "company_ids": [parseInt(companyId)],
            "dataset": dataset,
            "year": additionAttritionDate.getUTCFullYear(),
            "month": additionAttritionDate.getUTCMonth() + 1,
        }
        let config = {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken,
            },
            decompress: true,
            // withCredentials: true,
        };
        if (accessToken === null || accessToken === undefined || accessToken === '') {
            config['withCredentials'] = true;
        }

        try {
            const response = await axios.post(apiUrl, data, config);
            let dateStr = '';
            dateStr += additionAttritionDate.getUTCFullYear();
            if (additionAttritionDate.getUTCMonth() + 1  < 10) {
                dateStr += '-0' + (additionAttritionDate.getUTCMonth() + 1);
            }
            else {
                dateStr += '-' + (additionAttritionDate.getUTCMonth() + 1);
            }
            if (additionAttritionDate.getUTCDate() < 10) { 
                dateStr += '-0' + additionAttritionDate.getUTCDate();
            }
            else { 
                dateStr += '-' + additionAttritionDate.getUTCDate();
            }
            if (response.status == 200 && response.data.data.headcount_attritions[companyId][dateStr] !== undefined) {
                const attritionData = response.data.data.headcount_attritions[companyId][dateStr];
                const attritionDataSorted = attritionData.sort((a, b) => {
                    return sortOrder[a.seniority] - sortOrder[b.seniority];
                });
                setAttritionData(attritionDataSorted.slice(0, 25));

                if (userAccountDataStore.user !== null) {
                    // track growth event
                    apiEventsStore.trackApiEvents(userAccountDataStore.user.uuid, {
                        companyId,
                        eventType: 'growth',
                        eventData: {
                            "url": apiUrl,
                            "path": process.env.REACT_APP_API_ATTRITIONS_ENDPOINT,
                            "dataset": dataset,
                            "user_agent": navigator.userAgent,
                        },
                    });
                }
            }
            else {
                setAttritionData(null);
            }

        } catch (err) {
            console.log(err);
        }
        setIsAdditionAttritionLoading(false);
    };

    function getOptionsGrowth() {
        const filteredData = data.filter((item) => {
            return item.geo1 === selectedRegion.value;
        });
        const displayBenchmark = selectedRegion.value === 'All';
        const displayUniverseAvgs = universeAvgData !== null && showUniverseAvgs;

		const ltmHeadcountGrowthData = filteredData.map((item) => {
			return [item.the_date, parseFloat((item.ltm_net_hc_growth * 100).toFixed(1))];
		});
		const ltmEmployeeAdditionData = filteredData.map((item) => {
			return [item.the_date, parseFloat((item.ltm_addition_rate * 100).toFixed(1))];
		});
		const ltmEmployeeAttritionData = filteredData.map((item) => {
			return [item.the_date, parseFloat((item.ltm_attrition_rate * 100).toFixed(1))];
		});
        const ltmEmployeeGrowthProductivityData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.ltm_growth_productivity * 100).toFixed(1))];
        });
        const ltmAvgNetHCGrowthBenchmarkData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.avg_net_hc_growth_prev_12_mo_benchmark * 100).toFixed(1))];
        });
        const ltmAvgAttritionRateBenchmarkData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.avg_attrition_rate_prev_12_mo_benchmark * 100).toFixed(1))];
        });
        const ltmAvgAdditionRateBenchmarkData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.avg_addition_rate_prev_12_mo_benchmark * 100).toFixed(1))];
        });
        const ltmAvgGrowthProductivityBenchmarkData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.avg_growth_prod_prev_12_mo_benchmark * 100).toFixed(1))];
        });

        const dataSeries = [];
        if (showNetGrowthForGrowthChart) {
            dataSeries.push(
            {
                type: 'spline',
                name: 'Net Headcount Growth (Last 12 months)',
                data: ltmHeadcountGrowthData,
                color: lineColors['Net Headcount'],
            });
        }
        if (showHiringForGrowthChart) {
            dataSeries.push(
            {
                type: 'spline',
                name: 'Hiring Rate (Last 12 months)',
                data: ltmEmployeeAdditionData,
                color: lineColors['Hiring'],
            });
        }
        if (showAttritionForGrowthChart) {
            dataSeries.push(
            {
                type: 'spline',
                name: 'Attrition Rate (Last 12 months)',
                data: ltmEmployeeAttritionData,
                color: lineColors['Attrition'],
            });
        }
        if (showGrowthProdForGrowthChart) {
            dataSeries.push(
            {
                type: 'spline',
                name: 'Growth Efficiency (Last 12 months)',
                data: ltmEmployeeGrowthProductivityData,
                color: lineColors['Growth Efficiency'],
            });
        }
        
        if (displayBenchmark && showIndustry) {
            if (showNetGrowthForGrowthChart) {
                dataSeries.push(
                {
                    type: 'spline',
                    name: 'Benchmark: Avg Net HC Growth',
                    data: ltmAvgNetHCGrowthBenchmarkData,
                    color: lineColors['Net Headcount'],
                    dashStyle: 'Dash',
                });
            }
            if (showHiringForGrowthChart) {
                dataSeries.push(
                {
                    type: 'spline',
                    name: 'Benchmark: Avg Hiring Rate',
                    data: ltmAvgAdditionRateBenchmarkData,
                    color: lineColors['Hiring'],
                    dashStyle: 'Dash',
                });
            }
            if (showAttritionForGrowthChart) {
                dataSeries.push(
                {
                    type: 'spline',
                    name: 'Benchmark: Avg Attrition Rate',
                    data: ltmAvgAttritionRateBenchmarkData,
                    color: lineColors['Attrition'],
                    dashStyle: 'Dash',
                });
            }
            if (showGrowthProdForGrowthChart) {
                dataSeries.push(
                {
                    type: 'spline',
                    name: 'Benchmark: Avg Growth Efficiency',
                    data: ltmAvgGrowthProductivityBenchmarkData,
                    color: lineColors['Growth Efficiency'],
                    dashStyle: 'Dash',
                });
            }
        }

        if (displayUniverseAvgs) {
            const filteredUniverseAvgData = universeAvgData.filter((item) => {
                if (selectedRegion.value === 'All') {
                    return item.geo1 === "Global";
                }

                return item.geo1 === selectedRegion.value;
            });
            const ltmAvgHeadcountGrowthData = filteredUniverseAvgData.map((item) => {
                return [item.the_date, parseFloat((item.avg_ltm_net_hc_growth * 100).toFixed(1))];
            });
            const ltmAvgEmployeeAdditionData = filteredUniverseAvgData.map((item) => {
                return [item.the_date, parseFloat((item.avg_ltm_addition_rate * 100).toFixed(1))];
            });
            const ltmAvgEmployeeAttritionData = filteredUniverseAvgData.map((item) => {
                return [item.the_date, parseFloat((item.avg_ltm_attrition_rate * 100).toFixed(1))];
            });
            const ltmAvgEmployeeGrowthProductivityData = filteredUniverseAvgData.map((item) => {
                return [item.the_date, parseFloat((item.avg_ltm_growth_productivity * 100).toFixed(1))];
            });

            dataSeries.push(
                {
                    type: 'spline',
                    name: 'Net Headcount Growth (Last 12 months) - Peer Avg',
                    data: ltmAvgHeadcountGrowthData,
                    color: lineColors['Net Headcount'],
                    dashStyle: 'DotDotDash',
                },
                {
                    type: 'spline',
                    name: 'Hiring Rate (Last 12 months) - Peer Avg',
                    data: ltmAvgEmployeeAdditionData,
                    color: lineColors['Hiring'],
                    dashStyle: 'DotDotDash',
                },
                {
                    type: 'spline',
                    name: 'Attrition Rate (Last 12 months) - Peer Avg',
                    data: ltmAvgEmployeeAttritionData,
                    color: lineColors['Attrition'],
                    dashStyle: 'DotDotDash',
                },
                {
                    type: 'spline',
                    name: 'Growth Efficiency (Last 12 months) - Peer Avg',
                    data: ltmAvgEmployeeGrowthProductivityData,
                    color: lineColors['Growth Efficiency'],
                    dashStyle: 'DotDotDash',
                },
            );

        }


		return {
			chart: {
				zoomType: 'xy',
                type: 'spline',
                height: '33.3%',
                resetZoomButton: {
                    position: {
                        align: 'left',
                        verticalAlign: 'top',
                        x: 10,
                        y: -10,
                    }
                },
			},
			title: {
				text: 'Headcount Metrics',
				align: 'left'
			},
			subtitle: {
				text: document.ontouchstart === undefined ?
					'Click and drag in the plot area to zoom in' : 'Pinch the chart to zoom in',
				align: 'left'
			},
			xAxis: {
				type: 'datetime',
                tickInterval: 24 * 3600 * 1000 * 365,  // 1 year
			},
			yAxis: {
				title: {
					text: 'Rate (Last 12 months)'
				},
				labels: {
					format: '{value}%'
				}
			},
			legend: {
				enabled: true,
			},
			
            series: dataSeries,

			credits: {
				enabled: false
			},
            tooltip: {
                pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                shared: true,
                crosshairs: true, 
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: false
                    },
                    lineWidth: 2,
                    point: {
                        events: {
                            click: function (event) {
                                let date = new Date(event.point.x);
                                let dateStr = '' + date.getUTCFullYear() + '/' + (date.getUTCMonth() + 1) + '/' + date.getUTCDate();
                                setAdditionAttritionDate(new Date(dateStr));
                            }
                        }
                    },
                    events: {
                        // afterAnimate: function() {
                        //     if (!displayBenchmark) {
                        //         let chart = this.chart;
                        //         chart.renderer.text('Select All Regions to view benchmark data', 1100, 25)
                        //         .css({
                        //             fontSize: '14px',
                        //             color: 'gray',
                        //         })
                        //         .add();
                        //     }
                        // }
                    }
                },
            },

            loading: {
                hideDuration: 1000,
                showDuration: 1000
            },
	
		};
		
	};

    function getOptionsTotalHeadcount() {
        const filteredData = data.filter((item) => {
            return item.geo1 === selectedRegion.value;
        });
        const totalHeadcountData = filteredData.map((item) => {
            return [item.the_date, item.total_headcount];
        });
        const additionData = filteredData.map((item) => {
            let growthPct = item.new_headcount/item.total_headcount;
            return [item.the_date, parseFloat((growthPct * 100).toFixed(1))];
        });
        const attritionData = filteredData.map((item) => {
            let attritionPct = item.lost_headcount/item.total_headcount;
            return [item.the_date, parseFloat((-attritionPct * 100).toFixed(1))];
        });
        return {
			chart: {
				zoomType: 'xy',
                type: 'spline',
                height: '33.3%',
                resetZoomButton: {
                    position: {
                        align: 'left',
                        verticalAlign: 'top',
                        x: 10,
                        y: -10,
                    }
                },
			},
            tooltip: {
                shared: true,
            },
			title: {
				text: 'Total Headcount (Public Profiles)',
				align: 'left'
			},
			subtitle: {
				text: document.ontouchstart === undefined ?
					'Click and drag in the plot area to zoom in' : 'Pinch the chart to zoom in',
				align: 'left'
			},
			xAxis: {
				type: 'datetime',
                tickInterval: 24 * 3600 * 1000 * 365,  // 1 year
			},
			yAxis: [
                {
				    title: {
					    text: 'Total Headcount (Public Profiles)'
				    },
                    height: '50%',
                    lineWidth: 2,
			    },
                {
                    title: {
                        text: 'Monthly Hiring and Attrition Rates',
                    },
                    top: '55%',
                    height: '45%',
                    offset: 0,
                    lineWidth: 2,
                    labels: {
                        format: '{value}%'
                    },
                },
            ],
			legend: {
				enabled: true,
			},
			
			series: [
				{
					type: 'column',
					name: 'Total Heacount (Public Profiles)',
					data: totalHeadcountData,
                    color: lineColors['Total Headcount'],
                    pointWidth: 10,
                    tooltip: {
                        // format tooltip to show commas in numbers
                        pointFormatter: function() {
                            return '<span style="color:' + this.color + '">\u25CF</span> ' + this.series.name + ': <b>' + Highcharts.numberFormat(this.y, 0, undefined, ',') + '</b><br/>';
                        }
                    },
                    opacity: 0.8,
				},
                {  
                    type: 'column',
                    name: 'Hiring',
                    data: additionData,
                    yAxis: 1,
                    color: lineColors['Hiring'],
                    pointWidth: 10,
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                    },
                },
                {
                    type: 'column',
                    name: 'Attrition',
                    data: attritionData,
                    yAxis: 1,
                    color: lineColors['Attrition'],
                    pointWidth: 10,
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                    },
                },
			],

			credits: {
				enabled: false
			},

            plotOptions: {
                series: {
                    marker: {
                        enabled: false
                    },
                    lineWidth: 2,
                },
            },

            loading: {
                hideDuration: 1000,
                showDuration: 1000
            },
	
		};
    };

    function getOptionsGenderHeadcount() {
        const filteredData = data.filter((item) => {
            return item.geo1 === selectedRegion.value;
        });
        const ltmCompanyPctFemaleData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.company_hc_total_avg_f_prob * 100).toFixed(1))];
        });
        const ltmHiringPctFemaleData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.company_hc_new_avg_f_prob_prev_12_mo * 100).toFixed(1))];
        });
        const ltmAttritionPctFemaleData = filteredData.map((item) => {
            return [item.the_date, parseFloat((item.company_hc_lost_avg_f_prob_prev_12_mo * 100).toFixed(1))]; 
        });
        const publicProfileData = filteredData.map((item) => {
            return [item.the_date, item.total_headcount];
        });

        return {
			chart: {
				zoomType: 'xy',
                type: 'spline',
                height: '33.3%',
                resetZoomButton: {
                    position: {
                        align: 'left',
                        verticalAlign: 'top',
                        x: 10,
                        y: -10,
                    }
                },
			},
			title: {
				text: 'Gender Probability (Public Profiles)',
				align: 'left'
			},
			subtitle: {
				text: document.ontouchstart === undefined ?
					'Click and drag in the plot area to zoom in' : 'Pinch the chart to zoom in',
				align: 'left'
			},
			xAxis: {
				type: 'datetime'
			},
			yAxis: [
                {
                    title: {
                        text: 'Percentage Female'
                    },
                    labels: {
                        format: '{value}%'
                    },
			    },
                {   
                    gridLineWidth: 0,
                    title: {
                        text: 'Total Headcount (Public Profiles)'
                    },
                    opposite: true,
                    labels: {
                        formatter: function() {
                            if (this.value >= 1000) {
                                return (this.value/1000) + 'k';
                            }
                            else if ((this.value >= 1000000)) {
                                return (this.value/1000000) + 'M';
                            }
                            return this.value;
                        }
                    },
                    //min: 0,
                },
            ],
			legend: {
				enabled: true,
			},

			tooltip: {
                shared: true,
            },

			series: [
				{
					type: 'column',
					name: 'Public Profiles',
					data: publicProfileData,
                    yAxis: 1,
                    color: lineColors['Public Profiles'],
                    pointWidth: 10,
                    opacity: 0.8,
                    zoomType: 'xy',
                    tooltip: {
                        // format tooltip to show commas in numbers
                        pointFormatter: function() {
                            return '<span style="color:' + this.color + '">\u25CF</span> ' + this.series.name + ': <b>' + Highcharts.numberFormat(this.y, 0, undefined, ',') + '</b><br/>';
                        }
                    },
				},
                {
                    type: 'spline',
                    name: 'Pct Female, Company Avg',
                    data: ltmCompanyPctFemaleData,
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                    },
                },
                {
                    type: 'spline',
                    name: 'Pct Female, New Hires (Last 12 months)',
                    data: ltmHiringPctFemaleData,
                    color: lineColors['Hiring'],
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                    },
                },
                {
                    type: 'spline',
                    name: 'Pct Female, Attrits (Last 12 months)',
                    data: ltmAttritionPctFemaleData,
                    color: lineColors['Attrition'],
                    tooltip: {
                        pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}%</b><br/>',
                    },
                },
			],

			credits: {
				enabled: false
			},

            plotOptions: {
                series: {
                    marker: {
                        enabled: false
                    },
                    lineWidth: 2,
                },
            },

            loading: {
                hideDuration: 1000,
                showDuration: 1000
            },
	
		};
    };

    useEffect(() => {
        // check if user data is present or access token is provided
        if (accessToken === null || accessToken === undefined) {
            if (userAccountDataStore.user === null) {
                // accessToken is not used AND user is not logged in, redirect to login page
                navigate('/login');
            }
            else {
                // user is logged in refresh the data
                const refreshUserData = async () => {
                    try {
                        await userAccountDataStore.refreshUser(userAccountDataStore.user.email);
                        if (userAccountDataStore.unAuthorized) {
                            // redirect to login
                            console.log('unAuthorized');
                            navigate('/login');
                        }
                    } catch (err) {
                        console.log(err);
                    }
                };
                refreshUserData();
                if(userAccountDataStore.accessExpiresInDays <= 0) {
                    alert('Access expired. Redirecting to home page...');
                    navigate('/home');
                }
            }
        }
    }, []);

    useEffect(() => {
        try {
            if (userAccountDataStore.user !== null || accessToken !== null || accessToken !== undefined) {
                setIsMetaDataLoading(true);
                getMetaData();
            }
        }
        catch(err) {
            console.log(err);
        }
    }, []);

    useEffect(() => {
        try {
            if (publicProfileCount !== 0) {
                setIsPageLoading(true);
                getGrowthData();
            }
        }
        catch(err) {
            console.log(err);
        }
    }, [publicProfileCount]);

    useEffect(() => {
        try {
            if (publicProfileCount !== 0) {
                getAdditionsAttritionsData();
            }
        }
        catch(err) {
            console.log(err);
        }
    }, [additionAttritionDate]);

    useEffect(() => {
        try {
            if (data !== null
                && document.getElementById('total-headcount-chart-container') !== null
                && document.getElementById('company-gender-chart-container') !== null
            ) {
                let totalHeadcountOptions = getOptionsTotalHeadcount();
                Highcharts.chart('total-headcount-chart-container', totalHeadcountOptions);

                let companyGenderOptions = getOptionsGenderHeadcount();
                Highcharts.chart('company-gender-chart-container', companyGenderOptions);
            }
        }
        catch(err) {
            console.log(err);
        }
    }, [data, universeAvgData, isUnAuthorized, companyName, publicProfileCount, selectedRegion]);

    useEffect(() => {
        try {
            if (data !== null
                && document.getElementById('growth-chart-container') !== null
            ) {
                let growthChartOptions = getOptionsGrowth();
                Highcharts.chart('growth-chart-container', growthChartOptions);
            }
        }
        catch(err) {
            console.log(err);
        }
    }, [data, universeAvgData, isUnAuthorized, companyName, publicProfileCount, selectedRegion, showUniverseAvgs, showIndustry, showHiringForGrowthChart, showAttritionForGrowthChart, showNetGrowthForGrowthChart, showGrowthProdForGrowthChart]);


    const handleGeo1ChangeWithSelect = (selectedOption) => {
        if (selectedOption === null || selectedOption.length === 0) {
            console.warn('Atleast one region must be selected');
            return;
        }
        setSelectedRegion(selectedOption);
    };

    const customStyles = {
        control: (provided) => ({
        ...provided,
        width: 200,
        }),
        menu: (provided) => ({
        ...provided,
        width: 200,
        }),
    };

    function handleTabClick(path) {
        let type = '';
        if (path.includes("team")) {
            type = "Team Growth";
        }
        else if (path.includes("talent")) {
            type = "Talent Flow";
        }
        else if (path.includes("tenure")) {
            type = "Tenure";
        }
        else if (path.includes("geo")) {
            type = "Geo";
        }



        if (userAccountDataStore.user !== null && userAccountDataStore.user !== undefined) {
            mixpanelActions.track("Click", {
                location: "Growth",
                type: type,
                user: toJS(userAccountDataStore.user),
            });
            amplitudeActions.track("Click", {
                location: "Growth",
                type: type,
                user: toJS(userAccountDataStore.user),
            });
        }
        navigate(path);
    };

    if (isUnAuthorized) {
        return (
            <div style={{ textAlign: "center" }} >
                <p>Oops, something went wrong. Please contact your admin for more details.</p>
            </div>
        );
    }
    else if ( publicProfileCount === 0) {
        if (isMetaDataLoading) {
            return (
                <div style={{ textAlign: "center" }} >
                    <LoadingWithText texts={['Connecting to Telemetry API...', 'Requesting data...', 'Setting up app...']} interval={2000} />
                </div>
            );
        }
        else {
            return (
                <div style={{ textAlign: "center" }} >
                    <h1>No data</h1>
                    <p>Sorry, but the asset data you're requesting is currently unavailable.</p>
                </div>
            );
        }
    }
    else {
        let loadingTexts = ['Fetching company data...', 'Setting up filters...', 'Adding benchmark data...'];
        if (universeId !== null && universeId !== undefined) {
            loadingTexts.push('Adding universe data...');
        }
        loadingTexts.push('Finishing charts...');
        loadingTexts.push('Adding final touches...');
        return (
            <div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <span style={{ margin: '5px'}}/>
                        <div className="button active">
                            Company
                        </div>
                        <span style={{ margin: '5px'}}/>
                        <div className="button" onClick={()=> {handleTabClick(teamGrowthPath)}}>
                            Teams
                        </div>
                        <span style={{ margin: '5px'}}/>
                        <div className="button" onClick={() => {handleTabClick(talentFlowPath)}}>
                            Talent Flow
                        </div>
                        <span style={{ margin: '5px'}}/>
                        <div className="button" onClick={() => {handleTabClick(tenurePath)}}>
                            Tenure
                        </div> 
                        <span style={{ margin: '5px'}}/>
                        <div className="button" onClick={() => {handleTabClick(geoPath)}}>
                            Geo
                        </div> 
                    </div>
                    <div style={{display: 'flex', justifyContent: 'flex-end', padding: '10px', marginRight: '50px'}}>
                        {/* { universeId !== null && (
                            <NavLink 
                                style={{border: 'black 2px solid'}}
                                className='button'
                                to={universeSummaryPath}
                                state={{universeId: universeId, accessToken: accessToken}}
                                target="_blank" rel="noopener noreferrer"
                            >
                                View Universe
                            </NavLink>
                        )} */}
                        { userAccountDataStore.user !== null && <Dropdown collections={collections}/> }
                    </div>
                </div>
                <div style={{textAlign: 'center'}}>
                    <h1 style={{marginTop: '0px', marginBottom: '10px'}}>
                        Company Growth: {companyName}
                    </h1>
                </div>
                { isPageLoading ? (
                    <LoadingWithText texts={loadingTexts} interval={2000} />
                ) : (
                <div>
                <div className='checkbox-group-container'> 
                    <div className='checkbox-container'>
                        <label htmlFor='regions'>Region:</label>
                        &nbsp;&nbsp;
                        <Select
                            name="regions"
                            options={geo1Values.map((value) => ({value: value, label: value}))}
                            value={selectedRegion}
                            onChange={handleGeo1ChangeWithSelect}
                            styles={customStyles}
                        />
                    </div>
                </div>
                <br/>
                <div style={{textAlign: 'center'}}>
                    {/* { universeId !== null && (
                        <label>
                            <input type="checkbox" checked={showUniverseAvgs} onChange={() => setShowUniverseAvgs(!showUniverseAvgs)} />
                            &nbsp;Compare with Universe Average
                        </label>
                    )}
                    &nbsp;&nbsp;&nbsp;&nbsp; */}
                    <label>
                        <input type="checkbox" checked={showIndustry} onChange={() => setShowIndustry(!showIndustry)} />
                        &nbsp;Compare with Industry Benchmark
                    </label>
                </div>
                <br/>
                <br/>
                {
                    showIndustry && selectedRegion.value !== 'All'
                    ? (
                        <div style={{right: '450px', marginBottom: '50px', position: 'absolute'}}>
                            Select All Regions to view Benchmark data
                        </div>
                    )   
                    : null
                }
                <div className='bordered-box'>
                    <label>
                        <input type="checkbox" checked={showNetGrowthForGrowthChart} onChange={() => setShowNetGrowthForGrowthChart(!showNetGrowthForGrowthChart)} />
                        &nbsp;Net Headcount Growth
                    </label>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <label>
                        <input type="checkbox" checked={showHiringForGrowthChart} onChange={() => setShowHiringForGrowthChart(!showHiringForGrowthChart)} />
                        &nbsp;Hiring Rate
                    </label>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <label>
                        <input type="checkbox" checked={showAttritionForGrowthChart} onChange={() => setShowAttritionForGrowthChart(!showAttritionForGrowthChart)} />
                        &nbsp;Attrition Rate
                    </label>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <label>
                        <input type="checkbox" checked={showGrowthProdForGrowthChart} onChange={() => setShowGrowthProdForGrowthChart(!showGrowthProdForGrowthChart)} />
                        &nbsp;Growth Efficiency
                    </label>
                </div>
                <div className='chart-container'>
                    <div className="chart-container-left">
                        <div className="chart-container-left-child" id="growth-chart-container"></div>
                        <br/>
                        <div className="chart-container-left-child" id="total-headcount-chart-container"></div>
                        <br/>
                        <div className='chart-container-left-child' id="company-gender-chart-container"></div>
                        
                    </div>
                    <div className='chart-container-right'>
                        <div className = 'chart-container-right-child' id="addition-container">
                            <p> Click on the Headcount Metrics chart to list new hires and attritions for a particular time</p>
                            <h3> New Hires ({additionAttritionDate.toLocaleString('default',  {month: 'long', year: 'numeric'})})</h3>
                            { isAdditionAttritionLoading ? (
                                <LoadingWithText texts={['Requesting new hires data...', 'Preparing the list...', 'Finishing up...']} interval={1000} />
                            ) : additionData ? (
                                
                                <ul>
                                    { 
                                        additionData.map((item, index) => (
                                            <li key={index}>
                                                <a href= {item.linkedin_bug} rel="noreferrer" target="_blank">{item.title}</a>
                                            </li>
                                    ))
                                    }
                                </ul>
                            ) : (
                                <p> No data </p>
                            )}
                        </div>
                        <div className = 'chart-container-right-child' id="attrition-container">
                            <h3> Recent Attrits ({additionAttritionDate.toLocaleString('default',  {month: 'long', year: 'numeric'})})</h3>
                            { isAdditionAttritionLoading ? (
                                <LoadingWithText texts={['Requesting new attrits data...', 'Preparing the list...', 'Finishing up...']} interval={1000} />
                            ) : attritionData ? (
                                <ul>
                                    { 
                                        attritionData.map((item, index) => (
                                            <li key={index}>
                                                <a href= {item.linkedin_bug} rel="noreferrer" target="_blank">{item.title}</a>
                                            </li>
                                        ))
                                        
                                    }
                                </ul>
                            ) : (
                                <p> No data </p>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ margin:'0 auto', textAlign: 'center'}}>
                    <DownloadButton 
                        data={data} 
                        companyName={companyName} 
                        buttonText={ 'Download Company Data' }
                        fileName={ `${companyName}_company_data.csv` }
                        dataType={'companyGrowth'}
                        accessToken={accessToken}
                    />
                </div>
                <div style={{ margin: '10px'}}>
                    Notes:
                    <ul>
                        <li>Data as of {getLastDateOfMonth(dataVersionDateStore.dataVersionDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</li>
                        <li>All data and analysis is based on publicly available profiles</li>
                    </ul>
                </div>
                </div> )}
            </div>
        );
    }   
});

export default CompanyGrowthChartComponent;
